<template>
    <nav
        role="navigation"
        class="ml-2 border-l-2 border-white/10 pb-1 pl-2 pr-1"
    >
        <ul class="flex flex-col">
            <li v-if="sport === 'mbb'" :class="linkWrapClass()">
                <NavigationLink
                    label="Today's Games"
                    :link="route('bbnow', { sport })"
                    icon="wastebasket.png"
                    component="BBNow"
                    :tabindex="0"
                />
            </li>
            <li :class="linkWrapClass()">
                <NavigationLink
                    label="Conf/Team"
                    :link="
                        route(`${sport}schedules`, {
                            conference: selectedConferenceSlug ?? 'america-east',
                            team: selectedTeamSlug ?? 'all',
                        })
                    "
                    :icon="selectedTeam?.logo ?? 'notebook.png'"
                    :component="sport === 'mbb' ? 'MBB' : 'WBB'"
                    :tabindex="0"
                />
            </li>
            <li :class="linkWrapClass()">
                <NavigationLink
                    label="Standings"
                    :link="
                        route(`${sport}standings`, {
                            conference: selectedConferenceSlug ?? 'america-east',
                        })
                    "
                    icon="trophy.png"
                    :component="sport === 'mbb' ? 'MBBStandings' : 'WBBStandings'"
                    :tabindex="0"
                />
            </li>
            <li v-if="sport === 'mbb'" :class="linkWrapClass()">
                <NavigationLink
                    label="Rankings"
                    :link="route('mbbtop25')"
                    icon="clipboard.png"
                    component="MBBTop25"
                    :tabindex="0"
                />
            </li>
            <li v-if="showTeamSelector" class="mt-1 w-full px-1">
                <label for="teamSelector" class="text-xxs uppercase"
                    >Conf/Team</label
                ><br />
                <select
                    id="teamSelector"
                    class="w-full bg-slate-300 p-1 text-xs text-slate-900"
                    v-model="selectedValue"
                    @change="goToSelection"
                >
                    <optgroup
                        v-for="group in conferences"
                        :key="group.conference.slug"
                        :label="group.conference.name"
                    >
                        <option
                            v-for="t in group.teams"
                            :key="t.id"
                            :value="`${group.conference.slug}:${t.short_name}`"
                        >
                            {{ t.school }}<span v-if="t.rank"> ({{ t.rank }})</span>
                        </option>
                    </optgroup>
                </select>
            </li>
        </ul>
    </nav>
</template>

<script>
import BasketballNavigationLink from './BasketballNavigationLink.vue';
import { mapWritableState } from 'pinia';
import { useGeneralStore } from '@/utils/useGeneralStore';
import { basketballStore } from '@/utils/basketballStore';
import { router } from '@inertiajs/vue3';
import * as DataService from '@/DataService';

export default {
    name: 'BasketballNavigation',
    props: {
        sport: { type: String, required: true },
        conference: { type: [Object, String], default: null },
        team: { type: [Object, String], default: null },
    },
    components: { NavigationLink: BasketballNavigationLink },
    computed: {
        ...mapWritableState(useGeneralStore, ['showMobile']),
        ...mapWritableState(basketballStore, [
            'selectedTeam',
            'mbbConferences',
            'wbbConferences',
        ]),
        conferences() {
            return this.sport === 'mbb' ? this.mbbConferences : this.wbbConferences;
        },
        selectedConferenceSlug() {
            return (
                this.selectedValue?.split(':')[0] ??
                (typeof this.conference === 'string'
                    ? this.conference
                    : this.conference?.slug) ??
                null
            );
        },
        selectedTeamSlug() {
            return (
                this.selectedValue?.split(':')[1] ??
                (typeof this.team === 'string'
                    ? this.team
                    : this.team?.short_name?.toLowerCase()) ??
                null
            );
        },
        showTeamSelector() {
            const hiddenOn =
                this.sport === 'mbb'
                    ? ['MBBNow', 'MBBTop25', 'MBBStandings']
                    : ['WBBStandings'];

            return !hiddenOn.includes(this.$page.component);
        },
    },
    data() {
        return {
            selectedValue: null,
        };
    },
    created() {
        this.fetchTeamsAndConfs();
    },
    mounted() {
        const confSlug =
            typeof this.conference === 'string'
                ? this.conference
                : this.conference?.slug;
        const teamSlug =
            typeof this.team === 'string'
                ? this.team
                : this.team?.short_name?.toLowerCase();

        if (confSlug && teamSlug) {
            this.selectedValue = `${confSlug}:${teamSlug}`;
        }
        if (this.team) {
            this.selectedTeam = this.team;
        }
    },
    methods: {
        fetchTeamsAndConfs() {
            if (this.conferences.length) {
                return;
            }

            DataService.client.get(`/api/${this.sport}-teams-and-confs`).then((d) => {
                if (this.sport === 'mbb') {
                    this.mbbConferences = d.data;
                } else {
                    this.wbbConferences = d.data;
                }
            });
        },
        linkWrapClass() {
            return 'flex flex-wrap group mb-1';
        },
        goToSelection() {
            if (!this.selectedValue) {
                return;
            }

            const [conference, team] = this.selectedValue.split(':');
            router.visit(route(`${this.sport}schedules`, { conference, team }));
        },
    },
};
</script>
